import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import {
  Link, useLocation,
} from 'react-router-dom'
import {
  ArrowRight, Check, Clock, Copy, Loader2, Mail, RotateCcw, Search, Send, Sparkles, Users, X,
} from 'lucide-react'
import {
  referralService,
  scopeService,
} from '../services/api'
import {
  useReferralLink,
} from '../hooks/useReferralLink'
import {
  useReferralStatus,
} from '../hooks/useReferralStatus'
import {
  usePollWithBackoff,
} from '../hooks/usePollWithBackoff'
import {
  DAILY_LIMIT_FALLBACK,
  useReferralInvites,
} from '../hooks/useReferralInvites'
import type {
  ReferralInvitePreviewResponse,
  SuggestedContact,
} from '../client'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  AVATAR_TINTS,
  INVITE_SOURCE,
  STATUS_PILL,
  formatDollars,
  initials,
} from './referralShared'
import {
  hasAmbassadorEntry,
} from '../utils/referral'
import {
  referralProgramFromSearch,
} from '../utils/referralRouting'
import {
  hasScopeCluster,
} from '../utils/scopeClusters'

// How many contact cards are visible at once. Inviting or dismissing someone
// removes them from the pool, so the window auto-backfills — one goes out, the
// next slides in — keeping the grid full until the pool runs dry.
const VISIBLE_STEP = 18
// When the fetched batch has been worked down to this many, re-fetch to pull
// contacts from deeper in the server-side pool (a fetch returns up to ~100, so
// this fires after ~50 invites/dismissals) — repeating until the pool is dry.
const TOP_UP_THRESHOLD = 50
// While the background scanner is still building the network, re-poll so
// newly-discovered people appear without a manual refresh — but back off,
// because the ledger cannot change faster than the scanner commits to it. A
// scan run writes its contacts in ONE transaction at the end of the run, and
// on-demand runs are deduped to one per 15-minute bucket server-side
// (`_KICK_BUCKET_SECONDS`), so anything faster than that re-reads identical
// data. The first run — kicked by our own initial fetch — lands within a
// minute or two, so start responsive and double up to the scanner's cadence.
// A flat 25s poll here spent the endpoint's hourly budget ~12 minutes in and
// then 429'd for the rest of the hour, freezing the page it was meant to keep
// fresh; this schedule costs 8 requests in the first hour, then 4/hour.
const NETWORK_POLL_MIN_MS = 30_000
const NETWORK_POLL_MAX_MS = 15 * 60_000


/**
 * The full-page referral view at `/refer` — the "big list" counterpart to the
 * compact ReferralStrip on the Integrations page. Pre-populates the user's
 * whole network (up to ~100 contacts ranked by how often they actually meet
 * and email each person) as a dense card grid so they can walk the list and
 * send each invite with one click; each invite pulls the next contact into
 * view. Also carries the personal link, the reward the active programme pays,
 * and the live invited/opened/clicked funnel, so this page is shareable as a
 * single destination ("go to app.catchagent.ai/refer and work through your
 * list").
 *
 * Runs one of two programmes, chosen by `?program=`:
 *
 * - **standard** (default) — the referrer accrues hotel credit per qualifying
 *   referral and the friend gets 50% off their first month;
 * - **ambassador** (`?program=ambassador`) — the *invitee* earns $200 instead
 *   and the referrer earns nothing, with the investor-voiced invite copy.
 *
 * The choice drives the heading, the reward banner, the invite `source` (which
 * is what the backend re-derives the reward recipient from) and which saved
 * template the editor shows — so what a contact receives always matches what
 * the page promised.
 */
export function ReferralPage(): JSX.Element {
  // Which programme this visit is running. `/refer` is the standard programme
  // (the referrer earns the credit); `/refer?program=ambassador` is the legacy
  // Brand Ambassador one, where the invitee earns instead. The whole page
  // (copy, reward banner, invite source, template editor) follows this single
  // value, so the email a contact receives always matches what the page
  // promised.
  //
  // Derived from the router's location, not `window.location`: React Router
  // does not remount this component when only the query string changes, so
  // reading `window` once would leave a client-side hop between the two
  // programmes showing one programme's copy while sending the other's invites.
  const { search: locationSearch } = useLocation()
  const program = useMemo(
    () => referralProgramFromSearch(locationSearch),
    [locationSearch],
  )
  const scopeRecoveryState = useMemo(() => {
    const params = new URLSearchParams(locationSearch)
    if (params.get('scope_grant_incomplete') === '1') return 'incomplete'
    if (params.get('scope_upgrade_error') === 'access_denied') return 'cancelled'
    return null
  }, [locationSearch])
  const isAmbassador = program === 'ambassador'
  // Whether this session arrived from the ambassador offer page (marked in
  // ReferralRoute). A plain /refer visit never advertises the private offer.
  const cameFromAmbassadors = hasAmbassadorEntry()
  const { data: link } = useReferralLink()
  const { status } = useReferralStatus()
  const [network, setNetwork] = useState<SuggestedContact[]>([])
  const [networkLoading, setNetworkLoading] = useState(true)
  // The first load failing is NOT the same as an empty network: without this,
  // a rate-limited or blipped fetch fell through to the "No contacts to show
  // yet" copy and told a user with a full address book they had nobody.
  const [networkLoadFailed, setNetworkLoadFailed] = useState(false)
  // True while the backend scanner is still walking the user's mail/calendar
  // history — the pool keeps growing, so we poll and show a "still finding
  // people" hint until it flips false.
  const [scanInProgress, setScanInProgress] = useState(false)
  // Invites go out from the ambassador's OWN inbox, which needs the Gmail/Outlook
  // send scope. Sign-up only grants identity, so a new ambassador lands here
  // without it — null until checked, then false gates a "connect to send" prompt.
  const [mailConnected, setMailConnected] = useState<boolean | null>(null)
  useEffect(() => {
    if (!scopeRecoveryState || mailConnected !== false) return
    trackEvent(OnboardingEvents.REFERRAL_SCOPE_RECOVERY_SHOWN, {
      state: scopeRecoveryState,
    })
  }, [mailConnected, scopeRecoveryState])
  // The exact note each contact receives, from the user's own address. Fetched
  // from the backend so the preview can never drift from what's actually sent.
  const [preview, setPreview] = useState<ReferralInvitePreviewResponse | null>(null)
  // Editable invite subject/body (carrying the {name}/{link}/{code} tokens).
  // Seeded from the fetched template; `baseline*` mirrors what's persisted so
  // Save only lights up on a real change and "Saved" clears itself on the next
  // edit.
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [baselineSubject, setBaselineSubject] = useState('')
  const [baselineBody, setBaselineBody] = useState('')
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateResetting, setTemplateResetting] = useState(false)
  const [templateSaved, setTemplateSaved] = useState(false)
  const [search, setSearch] = useState('')
  const [alphabeticalOrder, setAlphabeticalOrder] = useState(false)
  const [visibleCount, setVisibleCount] = useState(VISIBLE_STEP)
  // Un-acted-on contacts left in the whole server-side pool (not just the
  // fetched batch) and the persisted dismissal count — both from /network.
  const [poolRemaining, setPoolRemaining] = useState(0)
  const [dismissedCount, setDismissedCount] = useState(0)
  const topUpInFlight = useRef(false)
  const removeFromNetwork = useCallback((email: string) => {
    setNetwork((prev) => prev.filter((c) => c.email.toLowerCase() !== email))
    setPoolRemaining((prev) => Math.max(0, prev - 1))
  }, [])
  const {
    invited,
    sending,
    inviteEmail,
    setInviteEmail,
    inviteSending,
    copied,
    error,
    dailyLimitReached,
    dailyLimitMessage,
    sendContactInvite,
    sendFreeformInvite,
    copyLink,
  } = useReferralInvites({
    contactInviteEvent: OnboardingEvents.REFERRAL_NETWORK_INVITE_SENT,
    removeFromList: removeFromNetwork,
    // Carries the programme to the backend, which re-derives the reward
    // recipient from it — the page never asserts who earns, it just says which
    // programme sent the invite.
    source: INVITE_SOURCE[program],
  })

  const applyNetworkResponse = useCallback(
    (res: Awaited<ReturnType<typeof referralService.getNetwork>>) => {
      setNetwork(res.contacts)
      setPoolRemaining(res.remaining_count)
      setDismissedCount(res.dismissed_count)
      setScanInProgress(res.scan_in_progress ?? false)
      setNetworkLoadFailed(false)
    },
    [],
  )

  const loadNetwork = useCallback(() => {
    setNetworkLoading(true)
    referralService
      .getNetwork()
      .then(applyNetworkResponse)
      .catch(() => setNetworkLoadFailed(true))
      .finally(() => setNetworkLoading(false))
  }, [applyNetworkResponse])

  // Separate from the network load below, which must not re-run on a
  // programme switch. This one must: React Router keeps this component mounted
  // across `/refer` ↔ `/refer?program=ambassador`, so a switch shows different
  // copy and a different offer, and that is a new page view of a different
  // variant. `program` is the only property carried — without it the two
  // programmes are indistinguishable in the funnel, since the URL query string
  // is the sole thing that differs between them.
  useEffect(() => {
    trackEvent(OnboardingEvents.REFERRAL_PAGE_VIEWED, { program })
  }, [program])

  useEffect(() => {
    loadNetwork()
  }, [loadNetwork])

  // Load a template response into both the editable fields and the persisted
  // baseline (used on first fetch and after a reset-to-default).
  const applyPreview = useCallback((p: ReferralInvitePreviewResponse) => {
    setPreview(p)
    setSubject(p.subject)
    setBody(p.body)
    setBaselineSubject(p.subject)
    setBaselineBody(p.body)
  }, [])

  useEffect(() => {
    referralService.getInvitePreview(program).then(applyPreview).catch(() => {})
  }, [applyPreview, program])

  const templateDirty = subject !== baselineSubject || body !== baselineBody

  const saveTemplate = async () => {
    if (!templateDirty || templateSaving) return
    setTemplateSaving(true)
    setTemplateSaved(false)
    try {
      const saved = await referralService.saveInviteTemplate(subject, body, program)
      setBaselineSubject(saved.subject)
      setBaselineBody(saved.body)
      setTemplateSaved(true)
    } catch {
      // Leave the edits in place; the unsaved state stays visible so the user
      // can retry.
    } finally {
      setTemplateSaving(false)
    }
  }

  const resetTemplate = async () => {
    if (templateResetting) return
    setTemplateResetting(true)
    setTemplateSaved(false)
    try {
      const def = await referralService.resetInviteTemplate(program)
      applyPreview(def)
    } catch {
      // No-op: keep the current template on failure.
    } finally {
      setTemplateResetting(false)
    }
  }

  useEffect(() => {
    let active = true
    scopeService
      .getStatus()
      .then((s) => { if (active) setMailConnected(hasScopeCluster(s, 'mail_calendar')) })
      .catch(() => { if (active) setMailConnected(null) })
    return () => { active = false }
  }, [])

  // While the scanner is still building the network, poll so freshly-discovered
  // people fill in on their own (a heavy user's list keeps growing for a while
  // after first load). Stops the moment the backend reports the scan is done.
  // Gated on a connected mailbox: without it the scanner can't run, so
  // scan_in_progress never resolves and polling would spin forever.
  const pollNetwork = useCallback(
    // A dropped tick is invisible: the next one re-reads the same ledger. The
    // hook swallows the rejection — a background refresh must never raise an
    // error over a page that is already showing good data.
    () => referralService.getNetwork().then(applyNetworkResponse),
    [applyNetworkResponse],
  )

  usePollWithBackoff({
    enabled: scanInProgress && mailConnected === true,
    onPoll: pollNetwork,
    minMs: NETWORK_POLL_MIN_MS,
    maxMs: NETWORK_POLL_MAX_MS,
  })

  // Auto top-up: once the fetched batch has been worked down past the
  // threshold and the server-side pool still has deeper contacts, re-fetch to
  // refill the grid. Repeats as the user keeps going, until the pool is dry.
  useEffect(() => {
    if (networkLoading || topUpInFlight.current) return
    if (network.length > TOP_UP_THRESHOLD) return
    if (poolRemaining <= network.length) return
    topUpInFlight.current = true
    referralService
      .getNetwork()
      .then(applyNetworkResponse)
      .catch(() => {})
      .finally(() => { topUpInFlight.current = false })
  }, [network.length, networkLoading, poolRemaining, applyNetworkResponse])

  // Dismiss ("not relevant"): optimistic removal + progress bump, persisted
  // fire-and-forget so the X feels instant. A dismissed contact never comes
  // back — the backend filters them from every referral surface.
  const dismissContact = (contact: SuggestedContact) => {
    removeFromNetwork(contact.email.toLowerCase())
    setDismissedCount((c) => c + 1)
    trackEvent(OnboardingEvents.REFERRAL_CONTACT_DISMISSED)
    referralService.dismissContact(contact.email).catch(() => {})
  }

  const matchingNetwork = useMemo(() => {
    const query = search.trim().toLowerCase()
    const matching = query
      ? network.filter(
        (c) =>
          c.name.toLowerCase().includes(query)
          || c.email.toLowerCase().includes(query),
      )
      : network

    if (!alphabeticalOrder) return matching
    return [...matching].sort(
      (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        || a.email.localeCompare(b.email, undefined, { sensitivity: 'base' }),
    )
  }, [alphabeticalOrder, network, search])
  // The visible window over the (filtered) pool. Invited/dismissed contacts
  // leave `network`, so the slice naturally pulls the next contact into view.
  const visibleNetwork = matchingNetwork.slice(0, visibleCount)
  const hiddenCount = matchingNetwork.length - visibleNetwork.length

  // "Worked through your network" progress: everyone acted on (invited or
  // dismissed) over everyone reachable. Mirrors the Integrations-page
  // abilities bar so the page reads as a completable checklist.
  const handledCount = invited.length + dismissedCount
  const totalNetwork = handledCount + poolRemaining
  const progressPct = totalNetwork > 0
    ? Math.round((handledCount / totalNetwork) * 100)
    : 0

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Hero: what this page is + the personal link */}
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shrink-0">
          <Users className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {isAmbassador ? 'Brand Ambassador Program' : 'Refer a friend'}
          </h1>
          <p className="text-sm text-gray-500">
            These are the people you actually meet and email. Invite anyone with one
            click, and a short, personal note goes out from your own email.
          </p>
        </div>
      </div>

      {link && (
        <div className="mt-4 flex items-stretch gap-2">
          <input
            type="text"
            readOnly
            value={link.referral_url}
            aria-label="Your personal Catch referral link"
            onFocus={(e) => e.currentTarget.select()}
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
          <button
            onClick={() => { if (link) void copyLink(link.referral_url) }}
            aria-label="Copy invite link"
            className="shrink-0 inline-flex items-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white text-sm font-medium px-3.5 py-2 rounded-lg shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.96]"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      )}

      {/* Invites send from the ambassador's own inbox, which needs the mail
          scope. Until it's granted, block the whole page with a modal — there's
          nothing useful to do here without it. Non-dismissible by design. */}
      {mailConnected === false && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="connect-email-title"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
            <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
              <Mail className="h-6 w-6" />
            </span>
            <h2 id="connect-email-title" className="text-lg font-bold text-gray-900">
              {scopeRecoveryState ? 'One more permission is needed' : 'Connect your email to continue'}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-500">
              {scopeRecoveryState === 'incomplete'
                ? 'Google did not grant every email permission Catch needs. Try again and select every requested permission.'
                : scopeRecoveryState === 'cancelled'
                  ? 'Email connection was cancelled. Try again when you are ready and select every requested permission.'
                  : 'Invites go out from your own inbox as a personal note. Connect your email so Catch can send them for you.'}
            </p>
            <button
              onClick={() => {
                if (scopeRecoveryState) {
                  trackEvent(OnboardingEvents.REFERRAL_SCOPE_RECOVERY_RETRY_CLICKED, {
                    state: scopeRecoveryState,
                  })
                }
                void scopeService.requestScopes(['mail_calendar'], '/refer', true)
              }}
              className="mt-5 inline-flex w-full items-center justify-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white text-sm font-medium px-3.5 py-2.5 rounded-lg shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.98]"
            >
              <Mail className="h-4 w-4" />
              {scopeRecoveryState ? 'Try again' : 'Connect email'}
            </button>
          </div>
        </div>
      )}

      {!isAmbassador && cameFromAmbassadors && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5">
          <span className="w-7 h-7 rounded-lg bg-white border border-amber-200 text-amber-700 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="flex-1">
            <p className="text-sm text-gray-600 leading-snug">
              <span className="font-semibold text-gray-900">Private ambassador offer:</span>{' '}
              earn <span className="font-semibold text-gray-900">$300 cash</span> for every
              referral who reaches their second monthly payment. Approval is required before
              you refer.
              {status && (
                <>
                  {' '}When Catch verifies the payout, it is settled in place of the{' '}
                  {formatDollars(status.program.hotel_credit_cents_per_referral)} standard hotel
                  credit for that referral.
                </>
              )}
            </p>
            <Link
              to="/ambassadors"
              onClick={() => trackEvent(OnboardingEvents.AMBASSADOR_OFFER_OPENED_FROM_REFER)}
              className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-amber-800 hover:text-amber-900"
            >
              See the offer and opt in
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2.5">
        <span className="w-7 h-7 rounded-lg bg-white border border-emerald-200 text-emerald-600 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </span>
        {isAmbassador ? (
          <p className="text-sm text-gray-600 leading-snug">
            <span className="font-semibold text-gray-900">Brand Ambassador:</span> the people you
            invite get{' '}
            <span className="font-semibold text-gray-900">$200 toward a hotel of their own</span>,
            on us, once they subscribe. You don&apos;t earn credit for these.
          </p>
        ) : (
          // The referrer-reward line is rendered from the backend's own
          // programme description rather than retyped, so the page, the invite
          // email and the day-3 referral email can never quote different
          // amounts. Until the status loads we show only the friend's
          // discount — always true, so it can't flicker into a claim we'd
          // have to take back.
          <p className="text-sm text-gray-600 leading-snug">
            {status
              ? status.program.summary
              : 'Your friends get 50% off their first month when they sign up '
                + 'through your link.'}
          </p>
        )}
      </div>

      {/* What each contact receives — editable, sent from the user's own inbox.
          Tokens are filled in per recipient at send time. */}
      {preview && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white shadow-soft overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                The message we&apos;ll send
              </span>
            </div>
            <span className="text-xs text-gray-400 truncate">
              From {preview.from_name} &lt;{preview.from_email}&gt;
            </span>
          </div>
          <div className="px-4 py-3 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Subject</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => { setSubject(e.target.value); setTemplateSaved(false) }}
                maxLength={255}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Message</span>
              <textarea
                value={body}
                onChange={(e) => { setBody(e.target.value); setTemplateSaved(false) }}
                rows={12}
                maxLength={5000}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 leading-relaxed focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-y"
              />
            </label>
            <p className="text-xs text-gray-400 leading-relaxed">
              Personalize with tokens — we fill them in for each recipient:{' '}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-gray-600">{'{name}'}</code> their first name,{' '}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-gray-600">{'{link}'}</code> your invite link,{' '}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-gray-600">{'{code}'}</code> your referral code.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { void saveTemplate() }}
                disabled={!templateDirty || templateSaving}
                className="inline-flex items-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-3.5 py-2 rounded-lg shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.96]"
              >
                {templateSaving
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Check className="w-4 h-4" />}
                Save
              </button>
              <button
                onClick={() => { void resetTemplate() }}
                disabled={templateResetting}
                className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium px-3.5 py-2 rounded-lg transition-colors"
              >
                {templateResetting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RotateCcw className="w-4 h-4" />}
                Reset to default
              </button>
              {!templateDirty && templateSaved && (
                <span className="text-xs font-medium text-emerald-600">Saved</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Network progress — same bar the Integrations page uses for abilities */}
      {!networkLoading && totalNetwork > 0 && (
        <div className="mt-5">
          <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5">
            <div
              className="bg-gradient-to-r from-primary-600 to-primary-500 h-2 rounded-full transition-[width] duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-primary-600">
            {handledCount === totalNetwork
              ? `You've reached everyone. All ${totalNetwork} contacts handled.`
              : `Reached ${handledCount} of ${totalNetwork} people in your network · ${progressPct}%`}
          </span>
        </div>
      )}

      {/* Manual invite + search/sort controls over the network list */}
      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your network…"
            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
        <div
          className="flex h-[38px] items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3"
          role="group"
          aria-label="Contact order"
        >
          <span className={`text-xs font-medium ${alphabeticalOrder ? 'text-gray-400' : 'text-gray-700'}`}>
            Default
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={alphabeticalOrder}
            aria-label="Sort contacts alphabetically"
            onClick={() => setAlphabeticalOrder((enabled) => !enabled)}
            className={`relative h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              alphabeticalOrder ? 'bg-primary-600' : 'bg-gray-300'
            }`}
          >
            <span
              aria-hidden="true"
              className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                alphabeticalOrder ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <span className={`text-xs font-medium ${alphabeticalOrder ? 'text-primary-600' : 'text-gray-400'}`}>
            A–Z
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void sendFreeformInvite() }}
            placeholder="Or invite by email…"
            className="flex-1 sm:w-56 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
          <button
            onClick={() => void sendFreeformInvite()}
            disabled={inviteSending || dailyLimitReached}
            aria-label="Send invite"
            className="shrink-0 inline-flex items-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white text-sm font-medium px-3.5 py-2 rounded-lg shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.96] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {inviteSending
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Send className="w-4 h-4" />}
            Send
          </button>
        </div>
      </div>

      {/* Daily cap reached: sends from the user's own inbox are paced to protect
          deliverability, so show the backend's message and pause invites for
          the day. Takes over from the generic error line while it's latched. */}
      {dailyLimitReached && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{dailyLimitMessage ?? DAILY_LIMIT_FALLBACK}</span>
        </div>
      )}

      {error && !dailyLimitReached && (
        <p className="mt-2 text-xs text-red-600">{error}</p>
      )}

      {/* The network list */}
      <div className="mt-4 rounded-xl border border-gray-200 bg-white shadow-soft overflow-hidden">
        {networkLoading && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Finding the people in your network…
          </div>
        )}

        {!networkLoading && networkLoadFailed && (
          <div className="py-10 px-6 text-center text-sm text-gray-500">
            <p>
              We couldn&apos;t load your network just now. You can still invite
              anyone by email above.
            </p>
            <button
              type="button"
              onClick={loadNetwork}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Try again
            </button>
          </div>
        )}

        {!networkLoading && !networkLoadFailed && network.length === 0 && scanInProgress && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Still finding the people in your network — they&apos;ll show up here as
            we go.
          </div>
        )}

        {!networkLoading && !networkLoadFailed && network.length === 0 && !scanInProgress && (
          <p className="py-10 px-6 text-center text-sm text-gray-500">
            No contacts to show yet. As you meet and email people through your
            connected calendar and inbox, they&apos;ll appear here. You can still
            invite anyone by email above.
          </p>
        )}

        {!networkLoading && network.length > 0 && visibleNetwork.length === 0 && (
          <p className="py-10 px-6 text-center text-sm text-gray-500">
            No one in your network matches “{search.trim()}”. You can invite them
            directly by email above.
          </p>
        )}

        {visibleNetwork.length > 0 && (
          <div className="p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {visibleNetwork.map((contact, i) => {
              const isSending = sending.has(contact.email)
              return (
                <div
                  key={contact.email}
                  title={`${contact.email} · ${contact.reason}`}
                  className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-lg pl-2 pr-1.5 py-1.5"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${AVATAR_TINTS[i % AVATAR_TINTS.length]}`}>
                    {initials(contact.name, contact.email)}
                  </div>
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="text-sm font-semibold text-gray-900 truncate">{contact.name}</div>
                    <div className="text-xs text-gray-500 truncate">{contact.role ?? contact.email}</div>
                  </div>
                  <button
                    onClick={() => dismissContact(contact)}
                    title={`Remove ${contact.name}`}
                    aria-label={`Remove ${contact.name}`}
                    className="w-7 h-7 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-200 flex items-center justify-center shrink-0 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => void sendContactInvite(contact)}
                    disabled={isSending || dailyLimitReached}
                    title={dailyLimitReached
                      ? "You've reached today's invite limit"
                      : `Send invite to ${contact.name}`}
                    aria-label={`Send invite to ${contact.name}`}
                    className="w-8 h-8 rounded-md bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white flex items-center justify-center shrink-0 shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.96] disabled:opacity-60"
                  >
                    {isSending
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Send className="w-4 h-4" />}
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {hiddenCount > 0 && (
          <button
            onClick={() => setVisibleCount((c) => c + VISIBLE_STEP)}
            className="w-full border-t border-gray-100 py-2.5 text-xs font-medium text-primary-600 hover:bg-primary-50 transition-colors"
          >
            Show {Math.min(hiddenCount, VISIBLE_STEP)} more · {hiddenCount} left in your network
          </button>
        )}

        {/* Scanner still running with people already shown: a quiet reassurance
            that more are on the way, without pulling focus from the list. */}
        {scanInProgress && visibleNetwork.length > 0 && (
          <div className="flex items-center justify-center gap-1.5 border-t border-gray-100 py-2 text-xs text-gray-400">
            <Loader2 className="w-3 h-3 animate-spin" />
            Still finding more people in your network…
          </div>
        )}
      </div>

      {/* People already invited, with the live engagement funnel */}
      {invited.length > 0 && (
        <div className="mt-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
            Invited
          </h2>
          <div className="rounded-xl border border-gray-200 bg-white shadow-soft p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {invited.map((contact) => {
              const pill = STATUS_PILL[contact.status]
              const PillIcon = pill.icon
              return (
                <div
                  key={contact.email}
                  title={contact.email}
                  className="flex items-center gap-2.5 bg-white border border-gray-200 rounded-lg pl-2 pr-2 py-1.5"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 bg-gray-100 text-gray-500">
                    {initials(contact.name, contact.email)}
                  </div>
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="text-sm font-semibold text-gray-700 truncate">
                      {contact.name ?? contact.email}
                    </div>
                    <div className="text-xs text-gray-400 truncate">{contact.email}</div>
                  </div>
                  <span
                    title={pill.title}
                    className={`inline-flex items-center gap-1 shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${pill.className}`}
                  >
                    <PillIcon className="w-3 h-3" />
                    {pill.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
